// src/pages/PBTalentAdmin.tsx: PB team talent admin (is_super_admin only)
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { supabase } from '../services/supabase';
import {
  C, Candidate, TalentJob, isCandidateComplete,
} from '../lib/talentConnect';
import { card, inputStyle, labelStyle, btnPrimary, btnGhost, Banner, LoadingScreen, fmtDate, SeniorityBadge, Pills } from '../lib/talentUI';
import { CandidateFields, CandidateFormState, candidatePayload } from '../lib/CandidateFields';

interface JobWithFirm extends TalentJob { firm: { name: string } | null; }
interface FlagRow {
  id: string; job_id: string; candidate_id: string; flagged_at: string;
  candidate: { full_name: string | null } | null;
  job: { title: string; firm: { name: string } | null } | null;
}

type Tab = 'candidates' | 'flag';

export default function PBTalentAdmin() {
  const { profile, loading: profileLoading } = useProfile();
  const [tab, setTab] = useState<Tab>('candidates');

  if (profileLoading) return <LoadingScreen />;
  if (!profile?.is_super_admin) {
    return (
      <div style={{ padding: '28px 32px', minHeight: '100vh', background: C.surface, fontFamily: 'system-ui' }}>
        <Banner type="err" text="This area is restricted to the Altveno admin team." />
      </div>
    );
  }

  return (
    <div style={{ padding: '28px 32px', minHeight: '100vh', background: C.surface, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ marginBottom: '18px' }}>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: C.black, fontWeight: 400, margin: '0 0 4px', letterSpacing: '-0.3px' }}>Talent Admin</h1>
        <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>Manage candidates and flag them to manager roles</p>
      </div>

      <div style={{ display: 'flex', gap: '2px', borderBottom: `1px solid ${C.border}`, marginBottom: '22px' }}>
        {([['candidates', 'Candidates'], ['flag', 'Flag to Job']] as [Tab, string][]).map(([key, lbl]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '10px 16px',
            fontSize: '13px', fontFamily: 'system-ui', fontWeight: tab === key ? 600 : 500,
            color: tab === key ? C.navy : C.muted,
            borderBottom: tab === key ? `2px solid ${C.navy}` : '2px solid transparent', marginBottom: '-1px',
          }}>{lbl}</button>
        ))}
      </div>

      {tab === 'candidates' ? <CandidatesTab /> : <FlagTab />}
    </div>
  );
}

// ── Candidates tab ───────────────────────────────────────────────────────────
function CandidatesTab() {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [visFilter, setVisFilter] = useState<'all' | 'visible' | 'hidden'>('all');
  const [editing, setEditing] = useState<Candidate | null>(null);

  useEffect(() => { load(); }, []);
  async function load() {
    setLoading(true);
    const { data } = await supabase.from('candidates').select('*').order('updated_at', { ascending: false });
    setCandidates((data as Candidate[]) || []);
    setLoading(false);
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return candidates.filter(c => {
      if (visFilter === 'visible' && !c.visible_on_platform) return false;
      if (visFilter === 'hidden' && c.visible_on_platform) return false;
      if (!q) return true;
      return [c.full_name, c.current_role, c.current_employer, ...(c.target_roles || []), ...(c.skills || [])]
        .filter(Boolean).some(v => String(v).toLowerCase().includes(q));
    });
  }, [candidates, search, visFilter]);

  if (loading) return <div style={{ color: C.muted, fontSize: '13px' }}>Loading candidates…</div>;

  if (editing) {
    return <EditCandidate candidate={editing} onClose={() => setEditing(null)} onSaved={c => { setEditing(null); setCandidates(prev => prev.map(x => x.id === c.id ? c : x)); }} />;
  }

  return (
    <div style={{ maxWidth: '960px' }}>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, role, employer, skills…" style={{ ...inputStyle, maxWidth: '340px' }} />
        <select value={visFilter} onChange={e => setVisFilter(e.target.value as any)} style={{ ...inputStyle, maxWidth: '180px' }}>
          <option value="all">All visibility</option>
          <option value="visible">Visible only</option>
          <option value="hidden">Hidden only</option>
        </select>
        <div style={{ marginLeft: 'auto', fontSize: '12px', color: C.muted, alignSelf: 'center', fontWeight: 600 }}>{filtered.length} candidate{filtered.length !== 1 ? 's' : ''}</div>
      </div>

      {filtered.length === 0 ? (
        <div style={{ ...card, padding: '40px', textAlign: 'center', fontSize: '13px', color: C.muted }}>No candidates found.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {filtered.map(c => (
            <div key={c.id} style={{ ...card, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div style={{ width: '38px', height: '38px', borderRadius: '4px', background: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.gold, fontFamily: 'Georgia, serif', fontWeight: 700, fontSize: '16px', flexShrink: 0 }}>{(c.full_name || '?').charAt(0)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13.5px', fontWeight: 600, color: C.navy }}>{c.full_name || 'Unnamed candidate'}</span>
                  <SeniorityBadge seniority={c.seniority} />
                  <span style={{ background: c.visible_on_platform ? '#F0FDF4' : C.surface, color: c.visible_on_platform ? C.success : C.muted, border: `1px solid ${c.visible_on_platform ? '#BBF7D0' : C.border}`, fontSize: '9px', fontWeight: 700, padding: '1px 6px', borderRadius: '2px', letterSpacing: '0.4px', textTransform: 'uppercase' }}>{c.visible_on_platform ? 'Visible' : 'Hidden'}</span>
                  {!c.profile_complete && <span style={{ color: C.amber, fontSize: '10px', fontWeight: 600 }}>Incomplete</span>}
                </div>
                <div style={{ fontSize: '12px', color: C.textSec }}>{c.current_role || '-'}{c.current_employer ? ` · ${c.current_employer}` : ''}</div>
              </div>
              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                <button onClick={() => navigate(`/talent-connect/candidate/${c.id}`)} style={{ ...btnGhost, padding: '6px 12px' }}>View</button>
                <button onClick={() => setEditing(c)} style={{ ...btnPrimary, padding: '6px 14px' }}>Edit</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EditCandidate({ candidate, onClose, onSaved }: { candidate: Candidate; onClose: () => void; onSaved: (c: Candidate) => void }) {
  const [form, setForm] = useState<CandidateFormState>({
    full_name: candidate.full_name || '', current_role: candidate.current_role || '', current_employer: candidate.current_employer || '',
    hide_employer: candidate.hide_employer, description: candidate.description || '',
    years_experience: candidate.years_experience != null ? String(candidate.years_experience) : '',
    seniority: (candidate.seniority as any) || 'mid',
    target_roles: candidate.target_roles || [], asset_classes: candidate.asset_classes || [],
    skills: candidate.skills || [], relocation_countries: candidate.relocation_countries || [],
  });
  const [visible, setVisible] = useState(candidate.visible_on_platform);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const save = async () => {
    setSaving(true); setMsg(null);
    const payload = candidatePayload(form);
    const { data, error } = await supabase.from('candidates').update({
      ...payload, visible_on_platform: visible,
      profile_complete: isCandidateComplete(payload), updated_at: new Date().toISOString(),
    }).eq('id', candidate.id).select().single();
    setSaving(false);
    if (error) { setMsg({ type: 'err', text: error.message }); return; }
    setMsg({ type: 'ok', text: 'Saved.' });
    onSaved(data as Candidate);
  };

  return (
    <div style={{ maxWidth: '820px' }}>
      <button onClick={onClose} style={{ ...btnGhost, marginBottom: '16px' }}>← Back to list</button>
      <div style={{ ...card, padding: '26px 28px' }}>
        <div style={{ fontFamily: 'Georgia, serif', fontSize: '17px', color: C.navy, marginBottom: '18px' }}>Edit candidate</div>
        {msg && <Banner type={msg.type} text={msg.text} />}
        <CandidateFields form={form} setForm={setForm} />
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: C.textSec, cursor: 'pointer', marginTop: '18px' }}>
          <input type="checkbox" checked={visible} onChange={e => setVisible(e.target.checked)} /> Visible on platform
        </label>
        <div style={{ marginTop: '22px', display: 'flex', gap: '8px' }}>
          <button onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>{saving ? 'Saving…' : 'Save'}</button>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Flag to Job tab ──────────────────────────────────────────────────────────
function FlagTab() {
  const { user } = useAuth();
  const [jobs, setJobs]           = useState<JobWithFirm[]>([]);
  const [candidates, setCands]    = useState<Candidate[]>([]);
  const [flags, setFlags]         = useState<FlagRow[]>([]);
  const [loading, setLoading]     = useState(true);

  const [jobSearch, setJobSearch]         = useState('');
  const [selectedJobIds, setSelectedJobIds] = useState<Set<string>>(new Set());
  const [candSearch, setCandSearch]       = useState('');
  const [selectedCandId, setSelectedCandId] = useState('');
  const [flagSearch, setFlagSearch]       = useState('');

  const [busy, setBusy]           = useState(false);
  const [msg, setMsg]             = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => { load(); }, []);
  async function load() {
    setLoading(true);
    const [{ data: jobData }, { data: candData }] = await Promise.all([
      supabase.from('talent_jobs').select('*, firm:firms(name)').eq('status', 'active').order('created_at', { ascending: false }),
      supabase.from('candidates').select('*').order('full_name', { ascending: true }),
    ]);
    setJobs((jobData as unknown as JobWithFirm[]) || []);
    setCands((candData as Candidate[]) || []);
    await loadFlags();
    setLoading(false);
  }

  async function loadFlags() {
    const { data } = await supabase.from('talent_job_flags')
      .select('id, job_id, candidate_id, flagged_at, candidate:candidates(full_name), job:talent_jobs(title, firm:firms(name))')
      .order('flagged_at', { ascending: false });
    setFlags((data as unknown as FlagRow[]) || []);
  }

  // Jobs matching the search, across ALL active jobs from ALL firms.
  const filteredJobs = useMemo(() => {
    const q = jobSearch.trim().toLowerCase();
    if (!q) return jobs;
    return jobs.filter(j =>
      [j.title, j.firm?.name, ...(j.target_roles || [])].filter(Boolean).some(v => String(v).toLowerCase().includes(q)),
    );
  }, [jobs, jobSearch]);

  // Candidate search (includes hidden), by name, current role, skills.
  const filteredCands = useMemo(() => {
    const q = candSearch.trim().toLowerCase();
    if (!q) return candidates.slice(0, 20);
    return candidates.filter(c =>
      [c.full_name, c.current_role, ...(c.skills || [])].filter(Boolean).some(v => String(v).toLowerCase().includes(q)),
    );
  }, [candidates, candSearch]);

  const selectedCand = useMemo(() => candidates.find(c => c.id === selectedCandId) || null, [candidates, selectedCandId]);

  // Jobs the selected candidate is already flagged to.
  const alreadyFlagged = useMemo(
    () => new Set(flags.filter(f => f.candidate_id === selectedCandId).map(f => f.job_id)),
    [flags, selectedCandId],
  );

  const filteredFlags = useMemo(() => {
    const q = flagSearch.trim().toLowerCase();
    if (!q) return flags;
    return flags.filter(f =>
      [f.candidate?.full_name, f.job?.title, f.job?.firm?.name].filter(Boolean).some(v => String(v).toLowerCase().includes(q)),
    );
  }, [flags, flagSearch]);

  const toggleJob = (id: string) => setSelectedJobIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const flagToSelected = async () => {
    if (!selectedCandId || selectedJobIds.size === 0 || !user) {
      setMsg({ type: 'err', text: 'Select a candidate and at least one job first.' });
      return;
    }
    setBusy(true); setMsg(null);
    const rows = [...selectedJobIds]
      .filter(id => !alreadyFlagged.has(id))
      .map(job_id => ({ job_id, candidate_id: selectedCandId, flagged_by: user.id }));
    if (rows.length === 0) {
      setBusy(false);
      setMsg({ type: 'err', text: 'Candidate is already flagged to all selected jobs.' });
      return;
    }
    const { error } = await supabase.from('talent_job_flags').insert(rows);
    setBusy(false);
    if (error) { setMsg({ type: 'err', text: error.message }); return; }
    await loadFlags();
    setSelectedJobIds(new Set());
    setMsg({ type: 'ok', text: `Flagged to ${rows.length} job${rows.length !== 1 ? 's' : ''}.` });
  };

  const removeFlag = async (id: string) => {
    await supabase.from('talent_job_flags').delete().eq('id', id);
    loadFlags();
  };

  if (loading) return <div style={{ color: C.muted, fontSize: '13px' }}>Loading…</div>;

  return (
    <div style={{ maxWidth: '980px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {msg && <Banner type={msg.type} text={msg.text} />}

      {/* 1 + 2, Job search + selectable results */}
      <div style={{ ...card, padding: '22px 24px' }}>
        <label style={labelStyle}>Search Jobs (all active roles, all firms)</label>
        <input value={jobSearch} onChange={e => setJobSearch(e.target.value)} placeholder="Search by job title, firm, target role…" style={inputStyle} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '16px 0 10px' }}>
          <span style={{ fontSize: '12px', color: C.muted, fontWeight: 600 }}>{filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''}</span>
          {selectedJobIds.size > 0 && (
            <button onClick={() => setSelectedJobIds(new Set())} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11.5px', color: C.navy, fontWeight: 600 }}>Clear selection ({selectedJobIds.size})</button>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '420px', overflowY: 'auto' }}>
          {filteredJobs.map(j => {
            const on = selectedJobIds.has(j.id);
            const flagged = selectedCandId ? alreadyFlagged.has(j.id) : false;
            return (
              <div key={j.id} onClick={() => toggleJob(j.id)} style={{
                display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '13px 15px', borderRadius: '4px',
                cursor: 'pointer', border: `1px solid ${on ? C.navy : C.borderLt}`, background: on ? '#F7F9FC' : C.white,
              }}>
                <input type="checkbox" checked={on} readOnly style={{ marginTop: '3px', flexShrink: 0, cursor: 'pointer', accentColor: C.navy }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'Georgia, serif', fontSize: '15px', color: C.navy }}>{j.title}</span>
                    {(j.seniority || []).map(s => <SeniorityBadge key={s} seniority={s} />)}
                    {flagged && <span style={{ fontSize: '9px', color: C.success, fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase' }}>Already flagged</span>}
                  </div>
                  <div style={{ fontSize: '12px', color: C.muted, marginTop: '3px' }}>{j.firm?.name || '-'}</div>
                  {(j.target_roles || []).length > 0 && <div style={{ marginTop: '8px' }}><Pills items={j.target_roles || []} max={6} /></div>}
                  {j.location && <div style={{ fontSize: '11px', color: C.mutedLt, marginTop: '6px' }}>{j.location}</div>}
                </div>
              </div>
            );
          })}
          {filteredJobs.length === 0 && <div style={{ fontSize: '12.5px', color: C.muted, padding: '20px', textAlign: 'center' }}>No active jobs match.</div>}
        </div>
      </div>

      {/* 3, Candidate selector */}
      <div style={{ ...card, padding: '22px 24px' }}>
        <label style={labelStyle}>Candidate to Flag (includes hidden)</label>
        <input value={candSearch} onChange={e => setCandSearch(e.target.value)} placeholder="Search by name, current role, skills…" style={inputStyle} />

        {selectedCand && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px', padding: '11px 13px', borderRadius: '4px', border: `1px solid ${C.navy}`, background: '#F7F9FC' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '13.5px', fontWeight: 600, color: C.navy }}>{selectedCand.full_name || 'Unnamed'}</span>
                <SeniorityBadge seniority={selectedCand.seniority} />
                {!selectedCand.visible_on_platform && <span style={{ fontSize: '9px', color: C.muted, fontWeight: 700, textTransform: 'uppercase' }}>Hidden</span>}
              </div>
              <div style={{ fontSize: '11.5px', color: C.textSec, marginTop: '2px' }}>{selectedCand.current_role || '-'}</div>
            </div>
            <button onClick={() => setSelectedCandId('')} style={{ ...btnGhost, padding: '5px 12px', flexShrink: 0 }}>Change</button>
          </div>
        )}

        {!selectedCand && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '12px', maxHeight: '260px', overflowY: 'auto' }}>
            {filteredCands.map(c => (
              <div key={c.id} onClick={() => setSelectedCandId(c.id)} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '3px', cursor: 'pointer', border: `1px solid ${C.borderLt}` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: C.text }}>{c.full_name || 'Unnamed'}</span>
                    <SeniorityBadge seniority={c.seniority} />
                    {!c.visible_on_platform && <span style={{ fontSize: '9px', color: C.muted, fontWeight: 600, textTransform: 'uppercase' }}>Hidden</span>}
                  </div>
                  <div style={{ fontSize: '11.5px', color: C.muted }}>{c.current_role || '-'}</div>
                </div>
              </div>
            ))}
            {filteredCands.length === 0 && <div style={{ fontSize: '12.5px', color: C.muted, padding: '16px', textAlign: 'center' }}>No candidates match.</div>}
          </div>
        )}
      </div>

      {/* 4, Flag action */}
      <div>
        <button onClick={flagToSelected} disabled={busy || !selectedCandId || selectedJobIds.size === 0} style={{
          ...btnPrimary, padding: '11px 24px', fontSize: '13px',
          opacity: busy || !selectedCandId || selectedJobIds.size === 0 ? 0.55 : 1,
          cursor: busy || !selectedCandId || selectedJobIds.size === 0 ? 'default' : 'pointer',
        }}>{busy ? 'Flagging…' : `Flag to Selected Jobs${selectedJobIds.size ? ` (${selectedJobIds.size})` : ''}`}</button>
      </div>

      {/* 5, Existing flags */}
      <div style={{ ...card, padding: '22px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
          <div style={{ ...labelStyle, marginBottom: 0 }}>Existing Flags ({flags.length})</div>
          <input value={flagSearch} onChange={e => setFlagSearch(e.target.value)} placeholder="Search flags by candidate, job, firm…" style={{ ...inputStyle, maxWidth: '320px' }} />
        </div>
        {filteredFlags.length === 0 ? (
          <div style={{ fontSize: '12.5px', color: C.muted, padding: '16px', textAlign: 'center' }}>{flags.length === 0 ? 'No flags yet.' : 'No flags match.'}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {filteredFlags.map(f => (
              <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', padding: '12px 14px', border: `1px solid ${C.borderLt}`, borderRadius: '3px' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: C.navy }}>{f.candidate?.full_name || 'Candidate'}</div>
                  <div style={{ fontSize: '11.5px', color: C.textSec }}>{f.job?.title || 'Role'}</div>
                  <div style={{ fontSize: '11px', color: C.muted }}>{f.job?.firm?.name || '-'} · {fmtDate(f.flagged_at)}</div>
                </div>
                <button onClick={() => removeFlag(f.id)} style={{ ...btnGhost, padding: '5px 10px', color: C.danger, borderColor: `${C.danger}44`, flexShrink: 0 }}>Remove</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
